// Section 11 education: schooling policy, subjects, school seats, teachers, students, literacy,
// and apprenticeships (master and apprentice pairs, with the control to set or end one). Props only.
import { useState } from 'preact/hooks';
import type { JobId, PersonId, SchoolSubject, SchoolingOption } from '../../sim/api';
import type { SocietyVM } from '../../viewModels/society';
import { societyText, subjectLabel } from '../text/en';
import { FactorTip } from './factor-tip';

const SCHOOLING: readonly SchoolingOption[] = [
  'none',
  'voluntary',
  'compulsory-12',
  'compulsory-14',
];

interface Props {
  education: SocietyVM['education'];
  apprenticeships: SocietyVM['apprenticeships'];
  masters: SocietyVM['masters'];
  apprentices: SocietyVM['apprentices'];
  canEdit: boolean;
  jobLabel: (job: JobId) => string;
  onSchooling: (option: SchoolingOption) => void;
  onSubjects: (first: SchoolSubject, second: SchoolSubject) => void;
  onApprentice: (apprentice: PersonId, master: PersonId | null) => void;
}

function pct(x: number): string {
  return `${Math.round(x * 100)}%`;
}

export function EducationPanel(props: Props) {
  const { education: e, canEdit, jobLabel } = props;
  const [apprentice, setApprentice] = useState<string>('');
  const [master, setMaster] = useState<string>('');
  const [first, second] = e.subjects;
  return (
    <div class="society-education">
      <dl class="society-stats">
        <dt>{societyText.seats}</dt>
        <dd class="num">{e.seats}</dd>
        <dt>{societyText.teachers}</dt>
        <dd class="num">{e.teachers}</dd>
        <dt>{societyText.students}</dt>
        <dd class="num">{e.students}</dd>
        <dt>{societyText.waiting}</dt>
        <dd class="num">{e.waiting}</dd>
        <dt>{societyText.literacy}</dt>
        <dd class="num">{pct(e.literacy)}</dd>
      </dl>
      {e.schools === 0 && <p class="society-warn">{societyText.noSchool}</p>}
      <div class="society-controls">
        <label>
          {societyText.schooling}{' '}
          <select
            value={e.schooling}
            disabled={!canEdit}
            onChange={(ev) => props.onSchooling(ev.currentTarget.value as SchoolingOption)}
          >
            {SCHOOLING.map((o) => (
              <option key={o} value={o}>
                {societyText.schoolingOption[o]}
              </option>
            ))}
          </select>
        </label>
        <fieldset class="society-subjects">
          <legend>{societyText.subjects}</legend>
          {[first, second].map((current, i) => (
            <select
              key={i}
              aria-label={societyText.subjectN(i + 1)}
              value={current}
              disabled={!canEdit}
              onChange={(ev) => {
                const value = ev.currentTarget.value as SchoolSubject;
                if (i === 0 && value !== second) props.onSubjects(value, second);
                if (i === 1 && value !== first) props.onSubjects(first, value);
              }}
            >
              {e.subjectOptions.map((s) => (
                <option key={s} value={s} disabled={s === (i === 0 ? second : first)}>
                  {subjectLabel(s, jobLabel)}
                </option>
              ))}
            </select>
          ))}
        </fieldset>
      </div>

      <h4>{societyText.apprenticeships}</h4>
      {props.apprenticeships.length === 0 ? (
        <p class="society-muted">{societyText.apprenticeshipsEmpty}</p>
      ) : (
        <table class="society-table">
          <thead>
            <tr>
              <th scope="col">{societyText.apprentice}</th>
              <th scope="col">{societyText.master}</th>
              <th scope="col">{societyText.job}</th>
              <th scope="col">{societyText.colSkills}</th>
              <th scope="col">{societyText.colGain}</th>
              <th scope="col">
                <span class="visually-hidden">{societyText.colActions}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {props.apprenticeships.map((r) => (
              <tr key={r.apprenticeId} class={r.active ? '' : 'is-inactive'}>
                <th scope="row">{r.apprentice}</th>
                <td>{r.master}</td>
                <td>
                  {jobLabel(r.job)}
                  <span class="society-muted">
                    {' '}
                    ({r.active ? societyText.active : societyText.inactive})
                  </span>
                  {r.pending !== null && (
                    <span class="society-muted"> {societyText.thisSeason}</span>
                  )}
                </td>
                <td class="num">
                  {r.apprenticeSkill.toFixed(1)} / {r.masterSkill.toFixed(1)}
                </td>
                <td>
                  {r.gain !== null && (
                    <FactorTip
                      value={`+${r.gain.value.toFixed(2)}`}
                      tooltip={r.gain}
                      label={societyText.gainLabel(r.apprentice)}
                    />
                  )}
                </td>
                <td>
                  {r.pending !== 'end' && (
                    <button
                      type="button"
                      class="button button--small"
                      disabled={!canEdit}
                      onClick={() => props.onApprentice(r.apprenticeId, null)}
                    >
                      {societyText.end}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form
        class="society-controls"
        onSubmit={(ev) => {
          ev.preventDefault();
          if (apprentice === '' || master === '' || apprentice === master) return;
          props.onApprentice(apprentice as PersonId, master as PersonId);
          setApprentice('');
          setMaster('');
        }}
      >
        <label>
          {societyText.apprentice}{' '}
          <select
            value={apprentice}
            disabled={!canEdit}
            onChange={(ev) => setApprentice(ev.currentTarget.value)}
          >
            <option value="">{societyText.chooseApprentice}</option>
            {props.apprentices.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({jobLabel(p.job).toLowerCase()})
              </option>
            ))}
          </select>
        </label>
        <label>
          {societyText.master}{' '}
          <select
            value={master}
            disabled={!canEdit}
            onChange={(ev) => setMaster(ev.currentTarget.value)}
          >
            <option value="">{societyText.chooseMaster}</option>
            {props.masters.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({jobLabel(p.job).toLowerCase()} {p.skill.toFixed(1)})
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          class="button button--small"
          disabled={!canEdit || apprentice === '' || master === '' || apprentice === master}
        >
          {societyText.add}
        </button>
      </form>
    </div>
  );
}
